import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useSearchParams } from 'react-router';
import { syncEngine, type SyncStatus } from './sync/engine.ts';

/** A ref that always holds the latest value: lets an effect read current props without re-running when they change. */
export function useLatest<T>(value: T) {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  });
  return ref;
}

/**
 * Handle a one-shot URL parameter (?add=1 from a quick-add link, ?task=<id>
 * from a notification): run `action` with its value once, then drop it from
 * the URL so a reload or Back doesn't repeat it.
 */
export function useSearchParamAction(name: string, action: (value: string) => void) {
  const [params, setParams] = useSearchParams();
  const value = params.get(name);
  const latest = useLatest(action);
  useEffect(() => {
    if (value === null) return;
    latest.current(value);
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(name);
        return next;
      },
      { replace: true },
    );
  }, [name, value, setParams, latest]);
}

export function useOnline(): boolean {
  return useSyncExternalStore(
    (cb) => {
      window.addEventListener('online', cb);
      window.addEventListener('offline', cb);
      return () => {
        window.removeEventListener('online', cb);
        window.removeEventListener('offline', cb);
      };
    },
    () => navigator.onLine,
  );
}

export function useSyncStatus(): SyncStatus {
  return useSyncExternalStore(syncEngine.subscribe, syncEngine.getStatus);
}

/** Sets the document title; product name comes from branding. */
export function useDocumentTitle(title: string | null, productName: string) {
  useEffect(() => {
    document.title = title ? `${title} · ${productName}` : productName;
  }, [title, productName]);
}

/** Re-render on an interval (e.g. to keep "today" and relative times fresh). */
export function useNow(intervalMs = 60_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}
